import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import styled from 'styled-components'
import { InputCheckbox } from '@/components/common/inputs'
import { Typo } from '@/components/common/typography'
import { PayMethodButtons } from '@/components/domains/order/payment/benefit/PayMethodButtons'
import PaymentBenefitText from '@/components/domains/order/payment/benefit/PaymentBenefitText'
import { PaymentMethodTypeEnum } from '@/constants/payment-method-type.enum'
import { OrderFormType } from '@/containers/hooks/order/order-form-schema'
import { usePaymentMethodBenefits } from '@/containers/order/payment-method-benefits.hook'
import { UserContainer } from '@/containers/users/UserContainer'
import { PaymentMethodItem, PaymentMethodsWithBenefit } from '@/types/payment-method-benefit.type'
import { sessionTempPaymentMethodForFailureMobile } from '@/utils/utilSessionStorage'

/**
 * 결제 수단 리스트
 */
const paymentMethodItems: PaymentMethodItem[] = [
  {
    method: PaymentMethodTypeEnum.TossPay,
    icon: {
      name: 'tosspay',
      size: 74,
    },
  },
  {
    method: PaymentMethodTypeEnum.CreditCard,
  },
  {
    method: PaymentMethodTypeEnum.KbPay,
    icon: {
      name: 'kbpay',
      size: 56,
    },
  },
  {
    method: PaymentMethodTypeEnum.NaverPay,
    icon: {
      name: 'npay',
      size: 60,
    },
  },
  {
    method: PaymentMethodTypeEnum.Payco,
    icon: {
      name: 'payco',
      size: 65,
    },
  },
  {
    method: PaymentMethodTypeEnum.KakaoPay,
    icon: {
      name: 'kakaopay',
      size: 65,
    },
  },
  {
    method: PaymentMethodTypeEnum.VirtualAccount,
  },
  {
    method: PaymentMethodTypeEnum.Bank,
  },
]

interface PayMethodSectionProps {
  onPaymentMethodSelect: (payMethod: PaymentMethodTypeEnum) => void
  getPaymentMethod: () => PaymentMethodTypeEnum
}

export const PayMethodSection = ({ onPaymentMethodSelect, getPaymentMethod }: PayMethodSectionProps) => {
  const { setValue } = useFormContext<OrderFormType>()
  const { me } = UserContainer.useContainer()

  const { paymentMethodBenefits } = usePaymentMethodBenefits()
  const [isNextPayChecked, setIsNextPayChecked] = useState(true)

  const paymentMethodsWithBenefit: PaymentMethodsWithBenefit[] = paymentMethodItems.map((item) => {
    const benefit = paymentMethodBenefits?.find((benefit) => item.method === benefit.method)
    return {
      method: item.method,
      icon: item.icon,
      benefit: {
        contents: benefit?.contents,
      },
    }
  })

  const handleSelect = (payMethod: PaymentMethodTypeEnum) => {
    sessionTempPaymentMethodForFailureMobile.save(payMethod)
    onPaymentMethodSelect(payMethod)
    setValue('payMethodType', payMethod.toUpperCase())
  }

  const handleCheckUseMethodNextTime = (checked: boolean) => {
    setIsNextPayChecked(checked)
  }

  useEffect(() => {
    if (me?.payMethodType) {
      handleSelect(me.payMethodType.toLowerCase() as PaymentMethodTypeEnum)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me])

  return (
    <>
      <StyledSectionTitle>
        <Typo variant={'Body1 Bold'}>결제 수단</Typo>
      </StyledSectionTitle>
      <StyledPayMethodWrapper>
        <PayMethodButtons
          paymentMethodsWithBenefit={paymentMethodsWithBenefit}
          selectedMethod={getPaymentMethod()}
          onSelect={handleSelect}
        />
        <StyledPaymentBenefitText>
          <PaymentBenefitText
            paymentMethodsWithBenefit={paymentMethodsWithBenefit}
            selectedMethod={getPaymentMethod()}
          />
        </StyledPaymentBenefitText>
        <StyledInputCheckboxWrapper>
          <InputCheckbox
            id="next_pay_check"
            checked={isNextPayChecked}
            onChange={(e) => {
              handleCheckUseMethodNextTime(e.target.checked)
            }}
          >
            <Typo variant="Body2 Regular">다음에도 이 결제 수단 사용</Typo>
          </InputCheckbox>
        </StyledInputCheckboxWrapper>
      </StyledPayMethodWrapper>
    </>
  )
}

const StyledSectionTitle = styled.div`
  height: 4.8rem;
  padding: 1.2rem 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledPayMethodWrapper = styled.div`
  padding: 0 1.6rem 1.2rem;
`

const StyledPaymentBenefitText = styled.div`
  margin-top: 1.2rem;
`

const StyledInputCheckboxWrapper = styled.div`
  margin-top: 1.6rem;
`
